<template>
  <AppBody
    :subtitle="$t('reports.products-sales.title')"
    :actions="actions"
    :primaryLoading="exportCSVLoading"
    disableDivider
  >
    <CardModule untitle v-if="isAdmin">
      <ExpansionPanelFilters class="my-4">
        <v-form
          ref="form"
          @submit.prevent="filterSalesByProduct"
          class="search-forms"
        >
          <v-row>
            <v-col cols="12" md="3" sm="4">
              <BaseTextField
                v-model="filter.pesquisar"
                :placeholder="$t('reports.search')"
                append-icon="mdi-magnify"
                single-line
                hide-details
                clearable
                @input="handleSearch"
              />
            </v-col>

            <v-col cols="12" md="3" sm="4">
              <date-time-picker
                :label="$t('reports.initial-date')"
                @formatted-date="handleInitDateTime"
              ></date-time-picker>
            </v-col>

            <v-col cols="12" md="3" sm="4">
              <date-time-picker
                :label="$t('reports.final-date')"
                @formatted-date="handleEndDateTime"
              ></date-time-picker>
            </v-col>

            <v-col class="d-flex justify-end">
              <BaseButton
                type="submit"
                variant="filter"
                class="button rounded-xxl"
                :loading="salesByProductLoading"
              >
                {{ $t("reports.filter") }}
              </BaseButton>
            </v-col>
          </v-row>
        </v-form>
      </ExpansionPanelFilters>

      <v-row no-gutters>
        <v-checkbox
          v-model="filter.shipping"
          :label="$t('reports.products-sales.only-shipping')"
        ></v-checkbox>
      </v-row>

      <v-data-table
        class="rounded-lg"
        hide-details
        :no-data-text="$t('reports.no-info-found')"
        :loading-text="$t('reports.loading-info')"
        :headers="headers"
        :items="allSalesByProduct.info"
        :loading="salesByProductLoading"
        :items-per-page="10"
        :sort-by="['dthr_pagamento']"
        :sort-desc="[true]"
        mobile-breakpoint="0"
        :header-props="{
          class: 'rounded-t-lg',
          color: $config.dados.corinputs,
        }"
        :footer-props="{
          class: 'rounded-b-lg',
          itemsPerPageOptions: [10, 20, 50],
          itemsPerPageText: $t('reports.financial-producer.items-per-page'),
        }"
      >
        <template v-slot:item.dthr_pagamento="{ item }">
          {{ formatDateTime(item.dthr_pagamento) }}
        </template>
        <template v-slot:item.taxa_frete="{ item }">
          {{ formatCurrency(item.taxa_frete) }}
        </template>
      </v-data-table>
    </CardModule>
  </AppBody>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import dayjs from "dayjs";
import _ from "lodash";
import { formatCurrency, formatDateTime } from "@/utils";

import AppBody from "@/layouts/components/AppBody.vue";
import CardModule from "@/components/cards/CardModule.vue";
import BaseTextField from "@/components/forms/BaseTextField.vue";
import BaseButton from "@/components/forms/BaseButton.vue";
import DateTimePicker from "@/components/forms/DateTimePicker.vue";
import ExpansionPanelFilters from "@/components/forms/ExpansionPanelFilters.vue";

export default {
  name: "SalesByProduct",
  components: {
    AppBody,
    CardModule,
    BaseTextField,
    BaseButton,
    DateTimePicker,
    ExpansionPanelFilters,
  },
  data() {
    return {
      formatCurrency,
      formatDateTime,
      filter: {
        pesquisar: "",
        initDateTime: "",
        endDateTime: "",
        shipping: false,
      },
      actions: [
        {
          id: "CSV",
          name: "",
          icon: "mdi-microsoft-excel",
          action: () => this.exportSalesByProductCSV(this.filters),
        },
      ],
      dayjs: dayjs,
    };
  },
  computed: {
    ...mapGetters([
      "allSalesByProduct",
      "storeSequencial",
      "isAdmin",
      "exportCSVLoading",
      "salesByProductLoading",
    ]),
    headers() {
      return [
        {
          text: this.$t("reports.products-sales.customer"),
          align: "start",
          sortable: true,
          value: "nome",
          width: 160,
        },
        {
          text: this.$t("reports.products-sales.media"),
          align: "start",
          sortable: true,
          value: "midia",
          width: 140,
        },
        {
          text: this.$t("reports.products-sales.localizer"),
          align: "start",
          sortable: true,
          value: "localizador",
          width: 160,
        },
        {
          text: this.$t("reports.products-sales.product"),
          align: "start",
          sortable: true,
          value: "descricao",
          width: 200,
        },
        {
          text: this.$t("reports.products-sales.variation"),
          align: "start",
          sortable: true,
          value: "dsc_variacao",
          width: 200,
        },
        {
          text: "CEP",
          align: "start",
          sortable: true,
          value: "end_cep",
          width: 160,
        },
        {
          text: this.$t("reports.products-sales.state"),
          align: "start",
          sortable: true,
          value: "end_estado",
          width: 90,
        },
        {
          text: this.$t("reports.products-sales.city"),
          align: "start",
          sortable: true,
          value: "end_cidade",
          width: 160,
        },
        {
          text: this.$t("reports.products-sales.district"),
          align: "start",
          sortable: true,
          value: "end_bairro",
          width: 160,
        },
        {
          text: this.$t("reports.products-sales.street"),
          align: "start",
          sortable: true,
          value: "end_logradouro",
          width: 160,
        },
        {
          text: this.$t("reports.products-sales.number"),
          align: "end",
          sortable: true,
          value: "end_numero",
          width: 90,
        },
        {
          text: this.$t("reports.products-sales.order"),
          align: "start",
          sortable: true,
          value: "idpedido_marketplace",
          width: 180,
        },
        {
          text: this.$t("reports.products-sales.access"),
          align: "end",
          sortable: true,
          value: "acessos",
          width: 100,
        },
        {
          text: this.$t("reports.products-sales.shipping-fee"),
          align: "end",
          sortable: true,
          value: "taxa_frete",
          width: 130,
        },
      ];
    },
    filters() {
      let filter = {};

      if (this.filter.initDateTime) {
        Object.assign(filter, {
          data_ini: this.filter.initDateTime,
        });
      }

      if (this.filter.endDateTime) {
        Object.assign(filter, {
          data_fim: this.filter.endDateTime,
        });
      }

      if (this.filter.shipping) {
        Object.assign(filter, {
          frete: this.filter.shipping,
        });
      }

      Object.assign(filter, {
        producerId: this.storeSequencial,
        pesquisa: this.filter.pesquisar,
        frete: this.filter.shipping,
      });

      return filter;
    },
  },
  methods: {
    ...mapActions(["fetchSalesByProduct", "exportSalesByProductCSV"]),
    ...mapMutations(["setSalesByProductLoading"]),
    filterSalesByProduct() {
      this.setSalesByProductLoading(true);
      this.fetchSalesByProduct(this.filters);
    },
    handleSearch: _.debounce(function (event) {
      this.filter.pesquisar = event;
      this.filterSalesByProduct();
    }, 500),
    handleInitDateTime(formatteddatetime) {
      this.filter.initDateTime = formatteddatetime;
    },
    handleEndDateTime(formatteddatetime) {
      this.filter.endDateTime = formatteddatetime;
    },
  },
  mounted() {
    this.filterSalesByProduct();
  },
};
</script>

<style scoped></style>
